import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthorizationProvider, AuthorizationContext } from './contexts/AuthorizationContext';
import { useSignalR } from './hooks/useSignalR';
import Swal from 'sweetalert2';
import Login from './components/Login';
import Unauthorized from './components/Unauthorized';
import UserProfile from './components/UserProfile/UserProfile';
import SchoolDashboard from './components/Dashboard/SchoolDashboard';
import PPDDashboard from './components/Dashboard/PPDDashboard';
import AdminDashboard from './components/Dashboard/AdminDashboard';
import StudentUniform from './components/StudentUniform/StudentUniform';
import StudentMaster from './components/StudentMaster/StudentMaster';
import ReportSchoolMap from './components/ReportSchoolMap/ReportSchoolMap';
import MainLayout from './components/Layout/MainLayout';

// Master Data Management Components
import ActivityManagement from './components/MasterData/ActivityManagement';
import ActivitySchoolMapping from './components/MasterData/ActivitySchoolMapping';
import DunManagement from './components/MasterData/DunManagement';
import ParManagement from './components/MasterData/ParManagement';
import SchoolClassManagement from './components/MasterData/SchoolClassManagement';
import SchoolManagement from './components/MasterData/SchoolManagement';
import TahunManagement from './components/MasterData/TahunManagement';
import SchoolEnrolYearManagement from './components/MasterData/SchoolEnrolYearManagement';
import UserManagement from './components/MasterData/UserManagement';

// Authorization Management
import AuthorizationManagement from './components/AuthorizationManagement/AuthorizationManagement';

// Report Components
import ReportSummary from './components/ReportSummary/ReportSummary';
import ReportSchool from './components/ReportSchool/ReportSchool';
import ReportStudentListing from './components/ReportStudentListing/ReportStudentListing';
import ReportStudentList from './components/ReportStudentList/ReportStudentList';
import ReportPackingListShoes from './components/ReportPackingListShoes/ReportPackingListShoes';
import ReportPackingListShoesBySchool from './components/ReportPackingListShoesBySchool/ReportPackingListShoesBySchool';
import ReportPackingListUniform from './components/ReportPackingListUniform/ReportPackingListUniform';
import ReportPackingListUniformBySchool from './components/ReportPackingListUniformBySchool/ReportPackingListUniformBySchool';

// Audit Log Components
import SystemAuditLog from './components/SystemAuditLog/SystemAuditLog';
import UniformDataAuditLog from './components/UniformDataAuditLog/UniformDataAuditLog';

// Issue Reports Component
import IssueReports from './components/IssueReports/IssueReports';

// Bulk Promotion Components
import BulkPromotion from './components/BulkPromotion/BulkPromotion';
import ProcessTahun from './components/BulkPromotion/ProcessTahun';

// Protected Route wrapper with authorization checking
const ProtectedRoute = ({ children, allowedRoles, resourceCode }) => {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const authorization = useContext(AuthorizationContext);

  // Wait for both auth and authorization to load
  if (authLoading || authorization?.loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    console.log('ProtectedRoute: Not authenticated, redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role (role-based access)
  if (allowedRoles && !allowedRoles.includes(user?.role?.toUpperCase())) {
    console.log('ProtectedRoute: User role not allowed, redirecting to /unauthorized');
    return <Navigate to="/unauthorized" replace />;
  }

  // Check resource-level authorization (database-driven)
  if (resourceCode && authorization) {
    const hasResourceAccess = authorization.hasAccess(resourceCode);
    console.log('ProtectedRoute: Resource access check -', resourceCode, ':', hasResourceAccess);

    if (!hasResourceAccess) {
      // BACKWARD COMPATIBILITY: If resource not in auth database but user has required role, allow access
      // This matches the Sidebar behavior for gradual migration to database-driven authorization
      const userRole = user?.role?.toUpperCase();
      if (allowedRoles && allowedRoles.includes(userRole)) {
        console.log('ProtectedRoute: Granting access via role fallback for:', resourceCode, 'Role:', userRole);
        // Allow access via role - continue to render children
      } else {
        console.log('ProtectedRoute: No access to resource, redirecting to /unauthorized');
        return <Navigate to="/unauthorized" replace />;
      }
    }
  }

  console.log('ProtectedRoute: Access granted');
  return <MainLayout>{children}</MainLayout>;
};

// Placeholder components for routes (to be migrated from WebForms)
const Dashboard = () => <div>Dashboard (To be migrated)</div>;
const PPDAdmin = () => <div>PPD Admin (To be migrated)</div>;
const NotFound = () => <div>404 - Page Not Found</div>;

// AppContent component with SignalR integration
const AppContent = () => {
  const { isAuthenticated } = useAuth();
  const { isConnected, connectionState, subscribe, unsubscribe } = useSignalR();

  // Setup SignalR event listeners
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Handler for new issue notifications
    const handleNewIssue = (notification) => {
      console.log('[App] Received new issue notification:', notification);

      Swal.fire({
        title: 'New Issue Reported',
        html: `
          <div style="text-align: left;">
            <p><strong>Screen:</strong> ${notification.screen || 'N/A'}</p>
            <p><strong>Category:</strong> ${notification.category || 'N/A'}</p>
            <p><strong>Description:</strong> ${notification.description || 'N/A'}</p>
            <p><strong>Reported by:</strong> ${notification.reporterEmail || 'Unknown'}</p>
          </div>
        `,
        icon: 'info',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });
    };

    // Handler for new message notifications
    const handleNewMessage = (notification) => {
      console.log('[App] Received new message notification:', notification);

      Swal.fire({
        title: 'New Message',
        text: `You have a new message on issue #${notification.reportId}`,
        icon: 'info',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true
      });
    };

    // Handler for status change notifications
    const handleStatusChange = (notification) => {
      console.log('[App] Received status change notification:', notification);

      Swal.fire({
        title: 'Issue Status Updated',
        text: `Issue #${notification.reportId} status changed to: ${notification.newStatus}`,
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true
      });
    };

    // Handler for connection state changes
    const handleReconnecting = () => {
      console.log('[App] SignalR reconnecting...');
    };

    const handleReconnected = () => {
      console.log('[App] SignalR reconnected');

      Swal.fire({
        title: 'Reconnected',
        text: 'Real-time notifications are now active',
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    };

    const handleDisconnected = () => {
      console.log('[App] SignalR disconnected');
    };

    // Handler for student transfer notifications (received when a student is transferred OUT of your school)
    const handleStudentTransfer = (notification) => {
      console.log('[App] Received student transfer notification:', notification);

      Swal.fire({
        title: 'Pelajar Dipindahkan',
        html: `
          <div style="text-align: left;">
            <p><i class="fas fa-user-graduate" style="color: #856404; margin-right: 8px;"></i><strong>${notification.studentName}</strong></p>
            <p style="margin-left: 25px; color: #666;">IC: ****-****-${notification.studentICLast4}</p>
            <hr style="margin: 10px 0; border-color: #dee2e6;" />
            <p style="color: #dc3545;"><i class="fas fa-arrow-right" style="margin-right: 8px;"></i>Dipindahkan ke: <strong>${notification.toSchoolCode}</strong></p>
            <p style="font-size: 0.9em; color: #6c757d; margin-top: 10px;">
              Senarai pelajar akan dikemaskini secara automatik.
            </p>
          </div>
        `,
        icon: 'warning',
        confirmButtonText: 'Faham',
        confirmButtonColor: '#856404'
      });
    };

    // Subscribe to SignalR events
    subscribe('newIssue', handleNewIssue);
    subscribe('newMessage', handleNewMessage);
    subscribe('statusChange', handleStatusChange);
    subscribe('studentTransfer', handleStudentTransfer);
    subscribe('reconnecting', handleReconnecting);
    subscribe('reconnected', handleReconnected);
    subscribe('disconnected', handleDisconnected);

    // Log connection status
    console.log('[App] SignalR connection state:', connectionState, 'Connected:', isConnected);

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribe('newIssue', handleNewIssue);
      unsubscribe('newMessage', handleNewMessage);
      unsubscribe('statusChange', handleStatusChange);
      unsubscribe('studentTransfer', handleStudentTransfer);
      unsubscribe('reconnecting', handleReconnecting);
      unsubscribe('reconnected', handleReconnected);
      unsubscribe('disconnected', handleDisconnected);
    };
  }, [isAuthenticated, isConnected, connectionState, subscribe, unsubscribe]);

  return (
    <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Protected Routes - Role-based access (matches Login.aspx.cs redirect logic) */}

          {/* SUPERADMIN routes */}
          <Route
            path="/superadmin/dashboard"
            element={
              <ProtectedRoute allowedRoles={['SUPERADMIN']}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Activity Management routes - Resource-based authorization */}
          <Route
            path="/activity-management"
            element={
              <ProtectedRoute allowedRoles={['SUPERADMIN']} resourceCode="/activity-management">
                <ActivityManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activity/:activityId/schools"
            element={
              <ProtectedRoute allowedRoles={['SUPERADMIN']} resourceCode="/activity-management">
                <ActivitySchoolMapping />
              </ProtectedRoute>
            }
          />

          {/* Master Data Management routes - Resource-based authorization */}
          <Route
            path="/dun-management"
            element={
              <ProtectedRoute allowedRoles={['SUPERADMIN']} resourceCode="SuperAdmin-DUNManagement">
                <DunManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/par-management"
            element={
              <ProtectedRoute allowedRoles={['SUPERADMIN']} resourceCode="SuperAdmin-PARManagement">
                <ParManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/school-class-management"
            element={
              <ProtectedRoute allowedRoles={['SUPERADMIN']} resourceCode="SuperAdmin-SchoolClassManagement">
                <SchoolClassManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/school-management"
            element={
              <ProtectedRoute allowedRoles={['SUPERADMIN']} resourceCode="SuperAdmin-SchoolManagement">
                <SchoolManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tahun-management"
            element={
              <ProtectedRoute allowedRoles={['SUPERADMIN']} resourceCode="SuperAdmin-TahunManagement">
                <TahunManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/school-enrol-year-management"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'SUPERADMIN']} resourceCode="SuperAdmin-SchoolEnrolYearManagement">
                <SchoolEnrolYearManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <ProtectedRoute allowedRoles={['HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']} resourceCode="/user-management">
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/authorization-management"
            element={
              <ProtectedRoute allowedRoles={['SUPERADMIN']} resourceCode="/authorization-management">
                <AuthorizationManagement />
              </ProtectedRoute>
            }
          />

          {/* HEADMASTER and TEACHER routes */}
          <Route
            path="/school-dashboard"
            element={
              <ProtectedRoute allowedRoles={['HEADMASTER', 'TEACHER']}>
                <SchoolDashboard />
              </ProtectedRoute>
            }
          />

          {/* Bulk Student Promotion - HEADMASTER only */}
          <Route
            path="/bulk-promotion"
            element={
              <ProtectedRoute allowedRoles={['HEADMASTER']} resourceCode="/bulk-promotion">
                <BulkPromotion />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bulk-promotion/process/:tahun"
            element={
              <ProtectedRoute allowedRoles={['HEADMASTER']} resourceCode="/bulk-promotion">
                <ProcessTahun />
              </ProtectedRoute>
            }
          />

          {/* PPD routes */}
          <Route
            path="/ppd-dashboard"
            element={
              <ProtectedRoute allowedRoles={['PPD']}>
                <PPDDashboard />
              </ProtectedRoute>
            }
          />

          {/* ADMIN routes */}
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'SUPERADMIN']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* User Profile - accessible by all authenticated users */}
          <Route
            path="/user-profile"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'HEADMASTER', 'PPD', 'SUPERADMIN', 'TEACHER']} resourceCode="/user-profile">
                <UserProfile />
              </ProtectedRoute>
            }
          />

          {/* Default authenticated route */}
          <Route
            path="/student-uniform"
            element={
              <ProtectedRoute>
                <StudentUniform />
              </ProtectedRoute>
            }
          />

          {/* Student Master - Resource-based authorization */}
          <Route
            path="/student-master"
            element={
              <ProtectedRoute allowedRoles={['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']} resourceCode="/student-master">
                <StudentMaster />
              </ProtectedRoute>
            }
          />

          {/* School Map Report */}
          <Route
            path="/reports/school-map"
            element={
              <ProtectedRoute>
                <ReportSchoolMap />
              </ProtectedRoute>
            }
          />

          {/* Report Routes */}
          <Route
            path="/report-summary"
            element={
              <ProtectedRoute resourceCode="/report-summary">
                <ReportSummary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-school"
            element={
              <ProtectedRoute>
                <ReportSchool />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-student-listing"
            element={
              <ProtectedRoute>
                <ReportStudentListing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-student-list"
            element={
              <ProtectedRoute>
                <ReportStudentList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-packing-list-shoes"
            element={
              <ProtectedRoute>
                <ReportPackingListShoes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-packing-list-shoes-by-school"
            element={
              <ProtectedRoute>
                <ReportPackingListShoesBySchool />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-packing-list-uniform"
            element={
              <ProtectedRoute>
                <ReportPackingListUniform />
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-packing-list-uniform-by-school"
            element={
              <ProtectedRoute>
                <ReportPackingListUniformBySchool />
              </ProtectedRoute>
            }
          />

          {/* Audit Log Routes */}
          <Route
            path="/system-audit-log"
            element={
              <ProtectedRoute allowedRoles={['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']}>
                <SystemAuditLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/uniform-data-audit-log"
            element={
              <ProtectedRoute allowedRoles={['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']}>
                <UniformDataAuditLog />
              </ProtectedRoute>
            }
          />

          {/* Issue Reports Route */}
          <Route
            path="/issue-reports"
            element={
              <ProtectedRoute
                allowedRoles={['TEACHER', 'HEADMASTER', 'PPD', 'ADMIN', 'SUPERADMIN']}
                resourceCode="/issue-reports"
              >
                <IssueReports />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AuthorizationProvider>
          <AppContent />
        </AuthorizationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
